/**
 * Resolve Location / Schedule / HCS da evolução a partir do último
 * atendimento do profissional com o paciente. Fallback: agenda e local atuais.
 * Não copia preço, convênio nem URL de teleconsulta antiga.
 */
import dayjs from "dayjs";
import { oids } from "@/configs/Settings";
import { GetSchedule, GetSlot } from "@/services/fhir";
import { useFhirData } from "@/utils/useFhirData";
import { EVOLUTION_SLOT_DURATION_MINUTES } from "./constants";
import { isEvolutionAppointment } from "./isEvolutionAppointment";
import {
  appointmentsFromBundle,
  fetchPairAppointments,
} from "./evolutionAppointments";

const INVALID_STATUSES = ["cancelled", "noshow", "entered-in-error"];

export interface EvolutionActor {
  reference: string;
  display: string;
}

export interface EvolutionRefs {
  scheduleId: string;
  locationActor: EvolutionActor;
  healthCareServiceActor?: EvolutionActor;
  professionalActor: EvolutionActor;
  needsWhereby: boolean;
  slotDurationMinutes: number;
}

function actorOf(
  appointment: fhir5.Appointment | undefined,
  type: string,
): EvolutionActor | undefined {
  const actor = useFhirData.findParticipant(appointment?.participant, type);
  if (!actor?.reference) return undefined;
  return { reference: actor.reference, display: actor.display || "" };
}

function locationNeedsWhereby(
  locationRef: string,
  locations?: fhir5.BundleEntry<fhir5.Location>[],
): boolean {
  const id = locationRef.split("/")[1];
  const resource = locations?.find((l) => l.resource?.id === id)?.resource;
  const channel = resource?.extension?.find((ext) =>
    ext.url?.includes("attendance-channel"),
  )?.valueCode;
  if (channel === "teleatendimento" || channel === "chat") return true;
  return useFhirData.virtualService(resource);
}

function durationMinutes(appointment?: fhir5.Appointment): number {
  if (!appointment?.start || !appointment?.end) {
    return EVOLUTION_SLOT_DURATION_MINUTES;
  }
  const mins = dayjs(appointment.end).diff(dayjs(appointment.start), "minute");
  return mins > 0 && mins <= 120 ? mins : EVOLUTION_SLOT_DURATION_MINUTES;
}

function pickLastForRefs(bundle?: fhir5.Bundle): fhir5.Appointment | undefined {
  const list = appointmentsFromBundle(bundle).filter(
    (apt) => apt.status && !INVALID_STATUSES.includes(apt.status),
  );
  return (
    list.find((apt) => apt.status === "fulfilled") ||
    list.find((apt) => !isEvolutionAppointment(apt)) ||
    list[0]
  );
}

async function scheduleIdFromAppointment(
  appointment: fhir5.Appointment,
  bundle?: fhir5.Bundle,
): Promise<string | undefined> {
  const slotId = appointment.slot?.[0]?.reference?.split("/")?.[1];
  if (!slotId) return undefined;
  const fromBundle = bundle?.entry?.find(
    (e) => e.resource?.resourceType === "Slot" && e.resource?.id === slotId,
  )?.resource as fhir5.Slot | undefined;
  if (fromBundle?.schedule?.reference) {
    return fromBundle.schedule.reference.split("/")[1];
  }
  try {
    const slotResponse = await GetSlot({ id: slotId });
    const slot: fhir5.Slot | undefined =
      slotResponse?.resourceType === "Bundle"
        ? slotResponse?.entry?.[0]?.resource
        : slotResponse;
    return slot?.schedule?.reference?.split("/")?.[1];
  } catch (error) {
    console.error("scheduleIdFromAppointment", error);
    return undefined;
  }
}

function refsFromSchedule(
  schedule: fhir5.Schedule,
  professionalActor: EvolutionActor,
  last?: fhir5.Appointment,
  locations?: fhir5.BundleEntry<fhir5.Location>[],
): EvolutionRefs | null {
  const location =
    schedule.actor?.find((a) => a.reference?.includes("Location/")) ||
    actorOf(last, "Location");
  if (!location?.reference) return null;
  const hcs = schedule.actor?.find((a) =>
    a.reference?.includes("HealthcareService/"),
  );
  return {
    scheduleId: schedule.id || "",
    locationActor: {
      reference: location.reference,
      display: location.display || "",
    },
    ...(hcs?.reference && {
      healthCareServiceActor: {
        reference: hcs.reference,
        display: hcs.display || "",
      },
    }),
    professionalActor,
    needsWhereby: locationNeedsWhereby(location.reference, locations),
    slotDurationMinutes: durationMinutes(last),
  };
}

async function fallbackSchedules(
  professionalRef: string,
  organizationId?: string,
): Promise<fhir5.Schedule[]> {
  const bundle = await GetSchedule({
    actor: professionalRef,
    ...(organizationId && {
      organization: organizationId.includes("Organization/")
        ? organizationId
        : `${oids.cnpj}-${organizationId}`,
    }),
    active: true,
    count: 50,
  });
  return (bundle?.entry || [])
    .map((e) => e.resource)
    .filter((r): r is fhir5.Schedule => r?.resourceType === "Schedule");
}

/** Preferência: último Appointment. Fallback: Schedule ativo + Location. */
export async function resolveEvolutionReferences(params: {
  patientId: string;
  professionalActor: EvolutionActor;
  schedules?: fhir5.Schedule[];
  locations?: fhir5.BundleEntry<fhir5.Location>[];
  organizationId?: string;
  pairBundle?: fhir5.Bundle;
}): Promise<EvolutionRefs | null> {
  const { professionalActor, locations } = params;
  const bundle =
    params.pairBundle ||
    (await fetchPairAppointments({
      patientId: params.patientId,
      professionalRef: professionalActor.reference,
    }));
  const last = pickLastForRefs(bundle);
  if (last) {
    const locationActor = actorOf(last, "Location");
    const scheduleId = await scheduleIdFromAppointment(last, bundle);
    if (locationActor && scheduleId) {
      const hcs = actorOf(last, "HealthcareService");
      return {
        scheduleId,
        locationActor,
        ...(hcs && { healthCareServiceActor: hcs }),
        professionalActor,
        needsWhereby: locationNeedsWhereby(locationActor.reference, locations),
        slotDurationMinutes: durationMinutes(last),
      };
    }
  }
  const schedules =
    params.schedules?.filter((s) => s.active !== false && s.id) ||
    (await fallbackSchedules(
      professionalActor.reference,
      params.organizationId,
    ));
  const schedule = schedules.find((s) =>
    s.actor?.some((a) => a.reference?.includes("Location/")),
  );
  if (!schedule?.id) return null;
  return refsFromSchedule(schedule, professionalActor, last, locations);
}
